import { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Check, Search } from "lucide-react";

const base = "w-full rounded-lg border border-white/12 bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-azure focus:outline-none focus:ring-2 focus:ring-azure/40 transition-colors duration-200";

function useOutside(ref, cb) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, cb]);
}

export function Select({ value, onChange, options, placeholder = "Select…", testId }) {
  return (
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export function SearchSelect({ value, onChange, options, placeholder = "Search…", testId, allowCustom = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const filtered = options.filter((o) => o.toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} className={`${base} flex items-center justify-between text-left`}>
        <span className={value ? "text-ink" : "text-faint"}>{value || placeholder}</span>
        <ChevronDown size={15} className="text-faint" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/12 bg-base shadow-xl max-h-64 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
            <Search size={13} className="text-faint" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={testId ? `${testId}-search` : undefined}
              placeholder="Type to filter…" className="w-full bg-transparent text-sm text-ink focus:outline-none" />
          </div>
          <div className="overflow-y-auto">
            {allowCustom && q && !options.some((o) => o.toLowerCase() === q.toLowerCase()) && (
              <button type="button" onClick={() => { onChange(q); setOpen(false); setQ(""); }}
                className="w-full px-3 py-2 text-left text-sm text-azure hover:bg-white/5">Use “{q}”</button>
            )}
            {filtered.map((o) => (
              <button key={o} type="button" data-testid={testId ? `${testId}-opt-${o}` : undefined}
                onClick={() => { onChange(o); setOpen(false); setQ(""); }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-white/5 flex items-center justify-between ${o === value ? "text-azure" : "text-ink"}`}>
                {o} {o === value && <Check size={13} />}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-3 text-sm text-faint">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function MultiSelect({ values = [], onChange, options, placeholder = "Add…", testId, allowCustom = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const toggle = (o) => {
    if (values.includes(o)) onChange(values.filter((v) => v !== o));
    else onChange([...values, o]);
  };
  const filtered = options.filter((o) => o.toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-azure/15 px-2.5 py-1 text-xs text-ink" data-testid={testId ? `${testId}-chip-${v}` : undefined}>
            {v}<button type="button" onClick={() => toggle(v)} className="text-faint hover:text-signal"><X size={12} /></button>
          </span>
        ))}
      </div>
      <button type="button" onClick={() => setOpen(!open)} className={`${base} flex items-center justify-between text-left`}>
        <span className="text-faint">{placeholder}</span><ChevronDown size={15} className="text-faint" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/12 bg-base shadow-xl max-h-64 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
            <Search size={13} className="text-faint" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={testId ? `${testId}-search` : undefined}
              placeholder="Type to filter…" className="w-full bg-transparent text-sm text-ink focus:outline-none" />
          </div>
          <div className="overflow-y-auto">
            {allowCustom && q && !options.some((o) => o.toLowerCase() === q.toLowerCase()) && (
              <button type="button" onClick={() => { toggle(q); setQ(""); }}
                className="w-full px-3 py-2 text-left text-sm text-azure hover:bg-white/5">Add “{q}”</button>
            )}
            {filtered.map((o) => (
              <button key={o} type="button" data-testid={testId ? `${testId}-opt-${o}` : undefined}
                onClick={() => toggle(o)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-white/5 flex items-center justify-between ${values.includes(o) ? "text-azure" : "text-ink"}`}>
                {o} {values.includes(o) && <Check size={13} />}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-3 text-sm text-faint">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function CheckGroup({ values = [], onChange, options, testId }) {
  const toggle = (o) => values.includes(o) ? onChange(values.filter((v) => v !== o)) : onChange([...values, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)} data-testid={testId ? `${testId}-${o}` : undefined}
          className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${values.includes(o) ? "border-azure bg-azure/15 text-ink" : "border-white/15 text-muted hover:border-white/30"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}
